import { useMemo } from 'react';
import type { AdminUserPermissions } from '../../../../types/admin-users';

const DEFAULT_PERMISSIONS: AdminUserPermissions = {
  canDownloadCsv: false,
  canCreateUser: false,
  canBulkCreate: false,
  canEditUser: false,
  canToggleStatus: false,
  canResetPassword: false,
  canApproveUser: false,
  canRejectUser: false,
};

/**
 * Get menu list directly from sessionStorage (more reliable than Redux state)
 * This ensures permissions work even if sidebar component hasn't mounted yet
 */
function getMenuListFromSession(): any[] {
  try {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    if (!encoded) return [];
    const userInfo = JSON.parse(atob(encoded));
    return userInfo.menuList || [];
  } catch {
    return [];
  }
}

function extractFeatures(menuItems: any[] = []): Array<Record<string, any>> {
  if (!Array.isArray(menuItems)) {
    return [];
  }

  const adminMenu = menuItems.find((item) => item.code === 'ADMIN');
  if (!adminMenu || !Array.isArray(adminMenu.subList)) {
    return [];
  }

  const usersEntry = adminMenu.subList.find((sub: any) => sub.code === 'USERSLIST');
  if (!usersEntry) {
    return [];
  }

  const features = usersEntry?.features;
  if (!Array.isArray(features)) {
    return [];
  }

  return features;
}

function hasFeature(features: Array<Record<string, any>>, code: string): boolean {
  if (!Array.isArray(features)) {
    return false;
  }

  return features.some((feature) => {
    const featureCode = String(feature?.code ?? '').trim().toUpperCase();
    const targetCode = code.trim().toUpperCase();

    if (featureCode !== targetCode) {
      return false;
    }

    // If backend provides explicit view property, respect it; default to true otherwise.
    if (typeof feature?.view === 'boolean') {
      return feature.view;
    }

    return true;
  });
}

export function useAdminUserPermissions(): AdminUserPermissions {
  return useMemo(() => {
    // Read directly from sessionStorage for reliability
    const menuList = getMenuListFromSession();

    if (!menuList || menuList.length === 0) {
      return DEFAULT_PERMISSIONS;
    }

    const features = extractFeatures(menuList);

    return {
      canDownloadCsv: hasFeature(features, 'USERDOWNLOADCSV'),
      canCreateUser: hasFeature(features, 'CREATEUSER'),
      canBulkCreate: hasFeature(features, 'CREATEBULKUSERS'),
      canEditUser: hasFeature(features, 'UPDATEUSER'),
      canToggleStatus:
        hasFeature(features, 'USERDEACTIVATE') ||
        hasFeature(features, 'USERACTIVATE') ||
        hasFeature(features, 'USERLOCK'),
      canResetPassword: hasFeature(features, 'USERCHANGEPASSWORD'),
      // TODO: Remove these overrides after backend features are enabled
      canApproveUser: true, // hasFeature(features, 'USERAPPROVE'),
      canRejectUser: true, // hasFeature(features, 'USERREJECT'),
    };
  }, []); // Empty deps - sessionStorage doesn't trigger re-renders
}
